import { SelectFields } from '@repo/toolkit/types'

import {
  enrollmentClientContextJourneyFields,
  enrollmentClientContextJourneyInternalFields,
  enrollmentClientContextMainFields
} from '../../lib/types/fields/enrollments/enrollment'
import { Enrollment } from '../../lib/types/models/enrollment/enrollment'
import {
  EnrollmentInput,
  EnrollmentRequest
} from '../../lib/types/models/enrollment/enrollment-input'
import { SecureFileRequest } from '../../lib/types/models/file/file-input'
import { FieldsSelector, UpdateInput } from '../../lib/types/utils'
import { encodeNestedObject } from '../../lib/utils/functions/misc.functions'
import { API, constructApiUrl } from '../apis.config'
import { uploadFileSecurely, UploadPurpose } from '../media/secure-upload'
import { restApiClient } from '../rest-api-client'
import { getEnrollment } from './get-enrollment'

export async function updateEnrollment<T extends FieldsSelector<Enrollment>>(
  data: UpdateInput<EnrollmentInput>,
  fields: T
) {
  const { id, secureIdentityPicture, familyImages: secureFamilyImages, ...rest } = data
  let identityPicture: SecureFileRequest | null | undefined = undefined
  let familyImages: SecureFileRequest[] | undefined = undefined

  if (secureIdentityPicture?.file) {
    const uploadId = await uploadFileSecurely({
      file: secureIdentityPicture.file,
      purpose: UploadPurpose.ENROLLMENT_PICTURE
    })
    identityPicture = { uploadId, caption: secureIdentityPicture.caption }
  } else if (secureIdentityPicture === null) identityPicture = null

  if (secureFamilyImages) {
    familyImages = []
    for (const fileImage of secureFamilyImages) {
      if (fileImage.uploadId) {
        familyImages.push({ uploadId: fileImage.uploadId, caption: fileImage.caption })
        continue
      }

      if (fileImage.file) {
        const uploadId = await uploadFileSecurely({
          file: fileImage.file,
          purpose: UploadPurpose.ENROLLMENT_FAMILY_IMAGE
        })
        familyImages.push({ uploadId, caption: fileImage.caption })
      }
    }
  }

  return restApiClient
    .url(encodeNestedObject(constructApiUrl(API.enrollments.update.url, { id }), { fields }))
    .patch<
      SelectFields<Enrollment, T>,
      Partial<EnrollmentRequest>
    >({ ...rest, identityPicture, familyImages })
}

// the journey selection depends on who asks: the internal one carries the catalogue prices the
// backend serves to LEC and to the prestataire only
export async function updateEnrollmentClientContext(
  data: UpdateInput<EnrollmentInput>,
  journeyFields:
    | typeof enrollmentClientContextJourneyFields
    | typeof enrollmentClientContextJourneyInternalFields = enrollmentClientContextJourneyFields
) {
  const mainDetails = await updateEnrollment(data, enrollmentClientContextMainFields)
  const journeyDetails = await getEnrollment(data.id, journeyFields)

  return { ...mainDetails, ...journeyDetails }
}
